import { useCallback } from 'react';
import { useClient } from 'hooks/useClient';
import { useMutation } from 'react-query';
import { IContactPayload } from '@cincel/client';
import { useToast } from '@chakra-ui/react';
import { ERROR_TOAST_DEFAULTS, SUCCESS_TOAST_DEFAULTS } from '@helpers';
import fp from 'lodash/fp';
import { translations } from 'translations';
import { useTranslation } from 'react-i18next';
import { useSession } from 'store/session';
import { AxiosError } from 'axios';
import { getSessionTokenV3 } from 'helpers/sessionContext';


export const usePatch = () => {
  const client = useClient({
    token: getSessionTokenV3() as string,
    url: process.env.REACT_APP_API_V3,
  });

  const toast = useToast();

  const { team } = useSession();

  const { t } = useTranslation();

  const teamUUID = team?.uuid as string;

  const fn = async (payload: IContactPayload) => {
    await client.patchContact(
      teamUUID,
      fp.get('uuid')(payload),
      fp.pick(['email', 'name'])(payload),
    );

    const MESSAGE = t(translations.CONTACTS.PATCH_SUCCESS);

    toast({ ...SUCCESS_TOAST_DEFAULTS, title: MESSAGE });
  };

  const onError = useCallback(
    (error: AxiosError) => {
      let msg = t(translations.ERRORS.NETWORK_DEFAULT);
      if (error?.response?.status === 403) {
        msg = t(translations.ERRORS.PERMISSIONS);
      }
      if (error?.response?.status === 409) {
        msg = t(translations.CONTACTS.STATUS_CODE_409);
      }
      toast({ ...ERROR_TOAST_DEFAULTS, title: msg });
    },
    [t, toast],
  );

  const mutation = useMutation(['patch-contact', teamUUID], fn, { onError });

  return { ...mutation };
};
